import {
	DirectionType,
	ConditionDef,
	RuleDefinition,
	RuleExpr,
	UnifiedMessageType,
	UnifiedMessageTypeOption,
} from "../interfaces/IconDefinitions";
import { ElevatorUsage } from "../flatbuffers/generated/usage/elevator-usage";
import { EncodedMessageContent } from "../flatbuffers/generated/ln2/encoded-message-content";
import { BitMaskMessageContent } from "../flatbuffers/generated/ln2/bit-mask-message-content";
import { OneHotMessageContent } from "../flatbuffers/generated/ln2/one-hot-message-content";

export const DEFAULT_CONDITION: ConditionDef = {
	id: "",
	name: "",
	condition: {
		messageType: 0,
		contentType: 0,
		active: true,
		frontDoor: null,
		rearDoor: null,
		direction: null,
	},
};

export const DIRECTION_TYPES: DirectionType[] = [null, ElevatorUsage.Up, ElevatorUsage.Down];

const ONEHOT_OPTIONS: UnifiedMessageType[] = Object.values(OneHotMessageContent)
	.filter((v): v is OneHotMessageContent => typeof v === "number")
	.map((v) => ({
		contentType: EncodedMessageContent.OneHotMessage,
		messageType: v,
		label: OneHotMessageContent[v],
	}));

const BITMASK_OPTIONS: UnifiedMessageType[] = Object.values(BitMaskMessageContent)
	.filter((v): v is BitMaskMessageContent => typeof v === "number")
	.map((v) => ({
		contentType: EncodedMessageContent.BitMaskMessage,
		messageType: v,
		label: BitMaskMessageContent[v],
	}));

export const MESSAGES_TYPES_MERGED: UnifiedMessageType[] = [...ONEHOT_OPTIONS, ...BITMASK_OPTIONS];

export const MESSAGE_TYPE_OPTIONS: UnifiedMessageTypeOption[] = [
	// OneHot
	{
		contentType: EncodedMessageContent.OneHotMessage,
		messageType: OneHotMessageContent.Usage_UsageMessage,
		label: "Usage",
	},
	{
		contentType: EncodedMessageContent.OneHotMessage,
		messageType: OneHotMessageContent.Service_ServiceMessage,
		label: "Service",
	},

	// BitMask
	{
		contentType: EncodedMessageContent.BitMaskMessage,
		messageType: BitMaskMessageContent.Traffic_TrafficMessage,
		label: "Traffic",
	},
	{
		contentType: EncodedMessageContent.BitMaskMessage,
		messageType: BitMaskMessageContent.Status_StatusMessage,
		label: "Status",
	},
	{
		contentType: EncodedMessageContent.BitMaskMessage,
		messageType: BitMaskMessageContent.Control_ControlMessage,
		label: "Control",
	},
	{
		contentType: EncodedMessageContent.BitMaskMessage,
		messageType: BitMaskMessageContent.Fault_FaultMessage,
		label: "Fault",
	},
	{
		contentType: EncodedMessageContent.BitMaskMessage,
		messageType: BitMaskMessageContent.GroupSupervisoryStatus_GroupSupervisoryStatusMessage,
		label: "GroupSupervisoryStatus",
	},
	{
		contentType: EncodedMessageContent.BitMaskMessage,
		messageType: BitMaskMessageContent.GroupControl_GroupControlMessage,
		label: "GroupControl",
	},
	{
		contentType: EncodedMessageContent.BitMaskMessage,
		messageType: BitMaskMessageContent.ControllerInfo_ControllerInfoMessage,
		label: "ControllerInfo",
	},
];

// ---------------------------------------------------------------------------
// Shared condition builders
// ---------------------------------------------------------------------------

function usageCond(
	id: string,
	name: string,
	message: string,
	active: boolean,
	direction: DirectionType = null,
): ConditionDef {
	return {
		id,
		name,
		condition: {
			messageType: 0,
			contentType: 0,
			active,
			frontDoor: null,
			rearDoor: null,
			direction,
		},
	};
}

function ref(id: string): RuleExpr {
	return { kind: "ref", id };
}

function AND(...children: RuleExpr[]): RuleExpr {
	return { kind: "group", op: "AND", children };
}

function OR(...children: RuleExpr[]): RuleExpr {
	return { kind: "group", op: "OR", children };
}

const NO_REMOVE: RuleExpr = ref("NO_REMOVE");

// Idle gate: in door zone OR (not moving up AND not moving down)
function idleGate(prefix: string): {
	conditions: ConditionDef[];
	expr: RuleExpr;
} {
	return {
		conditions: [
			usageCond(`${prefix}_IN_DOOR_ZONE`, "In Door Zone", "InDoorZone", true),
			usageCond(`${prefix}_UP_INACTIVE`, "Usage Up Inactive", "Up", false),
			usageCond(`${prefix}_DOWN_INACTIVE`, "Usage Down Inactive", "Down", false),
		],
		expr: OR(ref(`${prefix}_IN_DOOR_ZONE`), AND(ref(`${prefix}_UP_INACTIVE`), ref(`${prefix}_DOWN_INACTIVE`))),
	};
}

// ---------------------------------------------------------------------------
// Default elevator icon rules
// ---------------------------------------------------------------------------

export const DEFAULT_ELEVATOR_RULES_OBJECT: RuleDefinition[] = (() => {
	// --- Up Icon ---
	// Cross-checks Usage:Down via display_map — the server sends
	// Usage:Down active=false before Usage:Up active=true.
	const upRule: RuleDefinition = {
		id: "UP_ICON",
		icon: "elUp",
		conditions: [
			usageCond("UP_ACTIVE", "Usage Up Active", "Up", true),
			usageCond("UP_DOWN_INACTIVE", "Usage Down Inactive", "Down", false),
		],
		activeWhen: AND(ref("UP_ACTIVE"), ref("UP_DOWN_INACTIVE")),
		removeWhen: NO_REMOVE,
	};

	// --- Down Icon ---
	const downRule: RuleDefinition = {
		id: "DOWN_ICON",
		icon: "elDown",
		conditions: [
			usageCond("DN_DOWN_ACTIVE", "Usage Down Active", "Down", true),
			usageCond("DN_UP_INACTIVE", "Usage Up Inactive", "Up", false),
		],
		activeWhen: AND(ref("DN_DOWN_ACTIVE"), ref("DN_UP_INACTIVE")),
		removeWhen: NO_REMOVE,
	};

	// --- Door Opening (transition — uses elOpening icon) ---
	const openingGate = idleGate("OPENING");
	const doorOpeningRule: RuleDefinition = {
		id: "DOOR_OPENING",
		icon: "elOpening",
		conditions: [
			usageCond("OPENING_FRONT_OPENING", "Front Door Opening", "FrontDoorOpening", true),
			...openingGate.conditions,
		],
		activeWhen: AND(ref("OPENING_FRONT_OPENING"), openingGate.expr),
		removeWhen: NO_REMOVE,
	};

	// --- Door Closing (transition — uses elOpening icon, same as opening) ---
	const closingGate = idleGate("CLOSING");
	const doorClosingRule: RuleDefinition = {
		id: "DOOR_CLOSING",
		icon: "elOpening",
		conditions: [
			usageCond("CLOSING_FRONT_CLOSING", "Front Door Closing", "FrontDoorClosing", true),
			...closingGate.conditions,
		],
		activeWhen: AND(ref("CLOSING_FRONT_CLOSING"), closingGate.expr),
		removeWhen: NO_REMOVE,
	};

	// --- Rear Door Opening (transition — uses elRearOpening icon) ---
	const rearOpeningGate = idleGate("REAR_OPENING");
	const rearDoorOpeningRule: RuleDefinition = {
		id: "REAR_DOOR_OPENING",
		icon: "elRearOpening",
		conditions: [
			usageCond("REAR_OPENING_REAR_OPENING", "Rear Door Opening", "RearDoorOpening", true),
			...rearOpeningGate.conditions,
		],
		activeWhen: AND(ref("REAR_OPENING_REAR_OPENING"), rearOpeningGate.expr),
		removeWhen: NO_REMOVE,
	};

	// --- Rear Door Closing (transition — uses elRearOpening icon, same as opening) ---
	const rearClosingGate = idleGate("REAR_CLOSING");
	const rearDoorClosingRule: RuleDefinition = {
		id: "REAR_DOOR_CLOSING",
		icon: "elRearOpening",
		conditions: [
			usageCond("REAR_CLOSING_REAR_CLOSING", "Rear Door Closing", "RearDoorClosing", true),
			...rearClosingGate.conditions,
		],
		activeWhen: AND(ref("REAR_CLOSING_REAR_CLOSING"), rearClosingGate.expr),
		removeWhen: NO_REMOVE,
	};

	// --- Door Open (single front door) ---
	const openGate = idleGate("OPEN");
	const doorOpenRule: RuleDefinition = {
		id: "DOOR_OPEN",
		icon: "elOpen",
		conditions: [usageCond("OPEN_FRONT_OPEN", "Front Door Opened", "FrontDoorOpened", true), ...openGate.conditions],
		activeWhen: AND(ref("OPEN_FRONT_OPEN"), openGate.expr),
		removeWhen: NO_REMOVE,
	};

	// --- Door Close (single front door) ---
	const closeGate = idleGate("CLOSE");
	const doorCloseRule: RuleDefinition = {
		id: "DOOR_CLOSE",
		icon: "elClose",
		conditions: [
			usageCond("CLOSE_FRONT_CLOSED", "Front Door Closed", "FrontDoorClosed", true),
			...closeGate.conditions,
		],
		activeWhen: AND(ref("CLOSE_FRONT_CLOSED"), closeGate.expr),
		removeWhen: NO_REMOVE,
	};

	// --- Double-door combos ---
	const fcrcGate = idleGate("FCRC");
	const frontCloseRearCloseRule: RuleDefinition = {
		id: "FRONT_CLOSE_REAR_CLOSE_ICON",
		icon: "elFrontCloseRearClose",
		conditions: [
			usageCond("FCRC_FRONT_CLOSED", "Front Door Closed", "FrontDoorClosed", true),
			usageCond("FCRC_REAR_CLOSED", "Rear Door Closed", "RearDoorClosed", true),
			...fcrcGate.conditions,
		],
		activeWhen: AND(ref("FCRC_FRONT_CLOSED"), ref("FCRC_REAR_CLOSED"), fcrcGate.expr),
		removeWhen: NO_REMOVE,
	};

	const fcroGate = idleGate("FCRO");
	const frontCloseRearOpenRule: RuleDefinition = {
		id: "FRONT_CLOSE_REAR_OPEN_ICON",
		icon: "elFrontCloseRearOpen",
		conditions: [
			usageCond("FCRO_FRONT_CLOSED", "Front Door Closed", "FrontDoorClosed", true),
			usageCond("FCRO_REAR_OPEN", "Rear Door Opened", "RearDoorOpened", true),
			...fcroGate.conditions,
		],
		activeWhen: AND(ref("FCRO_FRONT_CLOSED"), ref("FCRO_REAR_OPEN"), fcroGate.expr),
		removeWhen: NO_REMOVE,
	};

	const forcGate = idleGate("FORC");
	const frontOpenRearCloseRule: RuleDefinition = {
		id: "FRONT_OPEN_REAR_CLOSE_ICON",
		icon: "elFrontOpenRearClose",
		conditions: [
			usageCond("FORC_FRONT_OPEN", "Front Door Opened", "FrontDoorOpened", true),
			usageCond("FORC_REAR_CLOSED", "Rear Door Closed", "RearDoorClosed", true),
			...forcGate.conditions,
		],
		activeWhen: AND(ref("FORC_FRONT_OPEN"), ref("FORC_REAR_CLOSED"), forcGate.expr),
		removeWhen: NO_REMOVE,
	};

	const foroGate = idleGate("FORO");
	const frontOpenRearOpenRule: RuleDefinition = {
		id: "FRONT_OPEN_REAR_OPEN_ICON",
		icon: "elFrontOpenRearOpen",
		conditions: [
			usageCond("FORO_FRONT_OPEN", "Front Door Opened", "FrontDoorOpened", true),
			usageCond("FORO_REAR_OPEN", "Rear Door Opened", "RearDoorOpened", true),
			...foroGate.conditions,
		],
		activeWhen: AND(ref("FORO_FRONT_OPEN"), ref("FORO_REAR_OPEN"), foroGate.expr),
		removeWhen: NO_REMOVE,
	};

	return [
		upRule,
		downRule,
		doorOpeningRule,
		doorClosingRule,
		rearDoorOpeningRule,
		rearDoorClosingRule,
		doorOpenRule,
		doorCloseRule,
		frontCloseRearCloseRule,
		frontCloseRearOpenRule,
		frontOpenRearCloseRule,
		frontOpenRearOpenRule,
	];
})();

// ---------------------------------------------------------------------------
// Default escalator icon rules
// ---------------------------------------------------------------------------

export const DEFAULT_ESCALATOR_RULES_OBJECT: RuleDefinition[] = [
	{
		id: "ES_UP_ICON",
		icon: "esUp",
		conditions: [usageCond("ES_UP_ACTIVE", "Usage Up Active", "Up", true)],
		activeWhen: ref("ES_UP_ACTIVE"),
		removeWhen: NO_REMOVE,
	},
	{
		id: "ES_DOWN_ICON",
		icon: "esDown",
		conditions: [usageCond("ES_DOWN_ACTIVE", "Usage Down Active", "Down", true)],
		activeWhen: ref("ES_DOWN_ACTIVE"),
		removeWhen: NO_REMOVE,
	},
	{
		id: "ES_STOPPED_ICON",
		icon: "esStopped",
		conditions: [
			usageCond("ES_STOP_UP_INACTIVE", "Usage Up Inactive", "Up", false),
			usageCond("ES_STOP_DOWN_INACTIVE", "Usage Down Inactive", "Down", false),
		],
		activeWhen: AND(ref("ES_STOP_UP_INACTIVE"), ref("ES_STOP_DOWN_INACTIVE")),
		removeWhen: NO_REMOVE,
	},
];
